import { Directive, HostListener, Input } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env';

import { AssetService } from '../../models/assetservice.class';
import { ApiService } from '../../services/api/api.service';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { UtilsService } from '../../services/utils/utils.service';

@Directive({
    // tslint:disable-next-line: directive-selector
    selector: '[AppSelection]',
    standalone: false,
})
export class AppSelectionDirective {
    @Input() app: AssetService;
    @Input() package: AssetService;
    @Input() bridge: AssetService;
    @Input() report: {
        componentType: string;
        id: string;
        level: number;
        name: string;
        nextNodes: [];
    };
    @Input() navigateToAppHomeOnly: boolean;
    @Input() uiType: string;

    constructor(
        private _api: ApiService,
        private _utils: UtilsService,
        private _commonUtils: CommonUtilsService,
        private _router: Router
    ) {}

    @HostListener('click', ['$event'])
    onAppSelection = async (event?: Event) => {
        if (this.app?.assetType === 'PACKAGE') {
            this._utils.navigateToWrapperUI({
                packageApiName: this.app.restApiName,
            });
            return;
        }
        if (!this.bridge) {
            await this._api.permissions.transformAppWithPermissions(this.app);
        }
        if (this.app.assetType === 'BRIDGE_ASSET') {
            return;
        } else if (this.app.assetType === 'RECON') {
            return this._utils.navigateToApp({
                appApi: this.app.restApiName,
                bridgeApi: undefined,
                reportApi: undefined,
                switchToUi: 'enreconcile',
            });
        }
        event?.stopPropagation();
        if (['encomply', 'enreport', 'eninvoice', 'enreconcile'].includes(this.uiType)) {
            this.navigateToPlatformSupportedTag(this.uiType);
        } else {
            this.checkRouteBasedOnTag();
        }
    };

    private checkRouteBasedOnTag = async () => {
        const assetTags = this.app.tags || [];
        if (environment.restrictedProcessingUIs.includes('enreport')) {
            if (!assetTags.some(tag => tag.tagValue === 'eninvoice' && tag.tagKey === 'uitype')) {
                assetTags.push({ tagKey: 'uitype', tagValue: 'eninvoice' });
            }
        }
        const tagValues = assetTags
            .filter(t => t.tagKey === 'uitype')
            .reduce((list, tag) => {
                list.push(tag.tagValue);
                return list;
            }, [] as string[]);
        const currentPlatformAllowedTags = environment.tags;
        const href = window.location.href;
        const enComply = environment['encomply-ui'];
        const enReport = environment['enreport-ui'];
        const enInvoice = environment['eninvoice-ui'];
        const enReconcile = environment['enreconcile-ui'];
        if (!this.navigateToAppHomeOnly) {
            let platformSupportsTag = false;
            let found = false;
            tagValues?.forEach(tag => {
                if (platformSupportsTag) {
                    return;
                } else {
                    if (environment.restrictedProcessingUIs?.indexOf(tag.toLowerCase()) >= 0) {
                        tag = environment.defaultProcessingUI ? environment.defaultProcessingUI : 'enreport';
                    }
                    currentPlatformAllowedTags?.forEach((assetTag: string) => {
                        found = assetTag.toLowerCase() === tag.toLowerCase();
                    });
                    platformSupportsTag = platformSupportsTag || found;
                }
            });
            if (platformSupportsTag) {
                this.navigateToPlatformSupportedTag();
            } else {
                const supportedTag = ['eninvoice', 'enreport', 'enreconcile'].find(tag => tagValues.includes(tag));
                if (supportedTag) {
                    this.navigateToPlatformSupportedTag(supportedTag);
                    return;
                } else {
                    this.navigateToPlatformSupportedTag('enreport');
                }
            }
        } else if (this.navigateToAppHomeOnly) {
            this.setWindowRoute();
        } else if (
            href.indexOf(enInvoice) > -1 ||
            href.indexOf(enReport) > -1 ||
            href.indexOf(enComply) > -1 ||
            href.indexOf(enReconcile) > -1
        ) {
            this.navigateToPlatformSupportedTag();
        } else {
            this.setWindowRoute();
        }
    };

    private navigateToPlatformSupportedTag = (uiType?: string) => {
        const app = this.app;
        if (
            !app.canViewAllProcesses &&
            !app.canCreateNewProcess &&
            !app.canManageWorkflow &&
            !app.canViewIntergrations &&
            !app.canViewVDM
        ) {
            return this._utils.alertError('You donot have permissions for viewing the processes.');
        }
        const routes = [];
        const href = window.location.href;
        const enReport = environment['enreport-ui'];
        const enInvoice = environment['eninvoice-ui'];
        const enReconcile = environment['enreconcile-ui'];
        const isInEnInvoice = href.indexOf(enInvoice) > -1 || uiType === 'eninvoice';
        const isInEnRecon = href.indexOf(enReconcile) > -1 || uiType === 'enreconcile';
        if (uiType) {
            switch (uiType) {
                case 'encomply':
                    routes.push('enreport');
                    break;
                default:
                    routes.push(uiType);
                    break;
            }
        } else {
            if (isInEnInvoice) {
                routes.push('eninvoice');
            } else if (isInEnRecon) {
                routes.push('enreconcile');
            } else if (href.indexOf(enReport) > -1 || isInEnInvoice || isInEnRecon) {
                routes.push('enreport');
            }
        }
        routes.push('organizations', this._commonUtils.getFromStorage('currentOrganizationId'));
        isInEnRecon && (uiType === undefined || uiType === 'enreconcile') && routes.push('home');
        const queryParams: { [property: string]: string } = {};
        if (this.package) {
            routes.push('packages', this.package.restApiName);
        }
        if (this.bridge) {
            routes.push('bridges', this.bridge.restApiName);
        }
        if (app) {
            routes.push('apps', app.restApiName);
        }
        if (this.report) {
            routes.push('reports', encodeURIComponent(this.report.name));
        }
        if (!isInEnInvoice && !enReconcile) {
            if (this.report) {
                routes.push('report-transmissions');
            } else {
                if (href.indexOf(enReport) > -1) {
                    routes.push('processes');
                    if (!this.report) {
                        if (app.canViewAllProcesses) {
                            routes.push('state', 'all');
                        } else {
                            return this._utils.alertError('You donot have permissions for viewing the processes.');
                        }
                    }
                } else {
                    routes.push('home');
                }
            }
        } else if (!this.report) {
            routes.push('processes');
        }
        this._router.navigate(routes, {
            queryParams,
        });
    };

    private setWindowRoute = () => {
        let subRoute = '';
        const href = window.location.href;
        const enComply = environment['encomply-ui'];
        const enReport = environment['enreport-ui'];
        if (href.indexOf(enComply) > -1 || href.indexOf(enReport) > -1) {
            subRoute += 'organizations/' + this._commonUtils.getFromStorage('currentOrganizationId') + '/';
        }
        if (this.package?.restApiName) {
            subRoute += 'packages/' + this.package.restApiName + '/';
        }
        if (this.bridge?.restApiName) {
            subRoute += 'bridges/' + this.bridge.restApiName + '/';
        }
        if (this.app?.restApiName) {
            subRoute += 'apps/' + this.app.restApiName;
        }
        if (this.report?.name) {
            subRoute += '/reports/' + encodeURIComponent(this.report.name);
        }
        subRoute += '/home';
        this._commonUtils.setInStorage('app-home-redirect-url', window.location.pathname);
        this._router.navigateByUrl(subRoute);
    };
}
